import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { OperationStatus } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { OPERATION_STATUS_CHIP, SYSTEM_CHIP } from '../../templatesMeta';

type OperationStatusChipProps = {
    /** `null` on a system row — the chip then reads **System**. */
    status: OperationStatus | null;
    /** Row discriminator, appended after the literal testid prefix. */
    slot: string;
};

/**
 * The row's one-word state.
 *
 * A system row is not "active": it has no status, so it gets a neutral
 * **System** chip instead. That is the whole visual difference between the two
 * halves of this catalog carried in one cell — the read-only rows never wear a
 * colour that suggests a switch someone could flip.
 *
 * Blocked is muted rather than danger, for the reason the chart of accounts
 * mutes an archived account: retiring an operation is a decision, not a fault.
 */
export const OperationStatusChip: React.FC<OperationStatusChipProps> = ({
    status,
    slot,
}) => {
    const { t } = useTranslation('pages');
    const chip = status ? OPERATION_STATUS_CHIP[status] : SYSTEM_CHIP;

    return (
        <span
            className={cn(
                'inline-flex w-fit items-center rounded-lg px-2.5 py-1 text-xs font-medium',
                chip.className,
            )}
            {...getNodeTestIdProp(`operations-status-chip-${slot}`)}
        >
            {t(chip.labelKey)}
        </span>
    );
};
